// Fast Photos (C): shrink photos on the device before upload.
// 2560px long side / JPEG q0.82 ≈ 0.4–0.9 MB instead of 3–6 MB.
// A canvas re-encode strips EXIF, so capture time/GPS are read from the
// original file FIRST and returned for the caller to send as form fields.
//
// P0-1/P0-2: the resize runs in a Web Worker (OffscreenCanvas), ONE photo at a
// time, never on the main thread — 8 rapid full-sensor decodes on the main
// thread OOM-killed Android Chrome in the field. Older browsers fall back to
// the main-thread canvas path.
import { api } from "@/lib/api";

export const UPLOAD_MAX_SIDE = 2560;
export const UPLOAD_JPEG_QUALITY = 0.82;

// ---- Minimal JPEG EXIF reader (DateTimeOriginal + GPS) — best-effort ----
function exifDateToIso(s) {
  const m = (s || "").match(/^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]); // device-local wall time
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function parseTiff(dv, tiffStart, out) {
  const le = dv.getUint16(tiffStart) === 0x4949;
  const u16 = (o) => dv.getUint16(tiffStart + o, le);
  const u32 = (o) => dv.getUint32(tiffStart + o, le);
  const readIfd = (base) => {
    const n = u16(base);
    const entries = [];
    for (let i = 0; i < n && i < 200; i++) {
      const e = base + 2 + i * 12;
      entries.push({ tag: u16(e), type: u16(e + 2), count: u32(e + 4), valOff: e + 8 });
    }
    return entries;
  };
  const typeSize = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1 };
  const valPtr = (ent) => ((typeSize[ent.type] || 1) * ent.count <= 4 ? ent.valOff : u32(ent.valOff));
  const ascii = (o, n) => {
    let s = "";
    for (let i = 0; i < n - 1 && i < 64; i++) s += String.fromCharCode(dv.getUint8(tiffStart + o + i));
    return s;
  };
  const rat = (o) => u32(o) / (u32(o + 4) || 1);
  for (const ent of readIfd(u32(4))) {
    if (ent.tag === 0x8769) { // Exif sub-IFD
      for (const e2 of readIfd(u32(ent.valOff))) {
        if (e2.tag === 0x9003) out.taken = exifDateToIso(ascii(valPtr(e2), e2.count).trim());
      }
    } else if (ent.tag === 0x8825) { // GPS sub-IFD
      let latRef = "N", lngRef = "E", lat = null, lng = null;
      for (const g of readIfd(u32(ent.valOff))) {
        if (g.tag === 1) latRef = ascii(valPtr(g), g.count).trim() || "N";
        else if (g.tag === 2) { const p = valPtr(g); lat = rat(p) + rat(p + 8) / 60 + rat(p + 16) / 3600; }
        else if (g.tag === 3) lngRef = ascii(valPtr(g), g.count).trim() || "E";
        else if (g.tag === 4) { const p = valPtr(g); lng = rat(p) + rat(p + 8) / 60 + rat(p + 16) / 3600; }
      }
      if (lat != null && lng != null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        out.lat = latRef === "S" ? -lat : lat;
        out.lng = lngRef === "W" ? -lng : lng;
      }
    }
  }
}

export function extractExifMeta(buf) {
  const out = { taken: null, lat: null, lng: null };
  try {
    const dv = new DataView(buf);
    if (dv.byteLength < 4 || dv.getUint16(0) !== 0xffd8) return out; // JPEG only
    let off = 2;
    while (off + 4 <= dv.byteLength) {
      if (dv.getUint8(off) !== 0xff) break;
      const marker = dv.getUint8(off + 1);
      const len = dv.getUint16(off + 2);
      if (marker === 0xe1) { // APP1
        const sig = String.fromCharCode(dv.getUint8(off + 4), dv.getUint8(off + 5), dv.getUint8(off + 6), dv.getUint8(off + 7));
        if (sig === "Exif") parseTiff(dv, off + 10, out);
        break;
      }
      if (marker === 0xda || len < 2) break; // start of scan
      off += 2 + len;
    }
  } catch { /* malformed EXIF — blanks are fine */ }
  return out;
}

// ---- Worker resize: one at a time, never on the main thread ----
let resizeWorker = null;
let workerBroken = false;
const workerPending = new Map();
let resizeSeq = 0;
let resizeChain = Promise.resolve();

function getResizeWorker() {
  if (workerBroken) return null;
  if (resizeWorker) return resizeWorker;
  try {
    if (typeof Worker === "undefined" || typeof OffscreenCanvas === "undefined") { workerBroken = true; return null; }
    resizeWorker = new Worker(new URL("./imageResize.worker.js", import.meta.url));
    resizeWorker.onmessage = (e) => {
      const p = workerPending.get(e.data?.id);
      if (p) { workerPending.delete(e.data.id); p(e.data); }
    };
    resizeWorker.onerror = () => {
      workerBroken = true;
      const pend = [...workerPending.values()];
      workerPending.clear();
      pend.forEach((p) => p({ error: "worker" }));
    };
  } catch { workerBroken = true; resizeWorker = null; }
  return resizeWorker;
}

function resizeOffThread(blob) {
  const w = getResizeWorker();
  if (!w) return Promise.resolve(null);
  const run = () => new Promise((resolve) => {
    const id = ++resizeSeq;
    workerPending.set(id, resolve);
    try { w.postMessage({ id, blob, maxSide: UPLOAD_MAX_SIDE, quality: UPLOAD_JPEG_QUALITY }); }
    catch { workerPending.delete(id); resolve(null); }
  });
  // Serialize: at most one full-size decode alive at any moment.
  const out = resizeChain.then(run, run);
  resizeChain = out.catch(() => {});
  return out;
}

// Returns { blob, meta: {taken, lat, lng}, compressed, width, height }.
// Never throws — any decode failure returns the original file untouched.
export async function prepareImageForUpload(file) {
  const meta = { taken: null, lat: null, lng: null };
  try {
    const head = await file.slice(0, 256 * 1024).arrayBuffer(); // EXIF lives at the front
    Object.assign(meta, extractExifMeta(head));
  } catch { /* ignore */ }
  const type = (file.type || "").toLowerCase();
  const isJpeg = type.includes("jpeg") || type.includes("jpg");
  if (!isJpeg && !type.includes("png") && !type.includes("webp")) {
    return { blob: file, meta, compressed: false, width: 0, height: 0 }; // HEIC & co: server handles
  }
  const r = await resizeOffThread(file);
  if (r && !r.error) {
    if (r.skip) return { blob: file, meta, compressed: false, width: r.width || 0, height: r.height || 0 };
    if (r.blob) return { blob: r.blob, meta, compressed: true, width: r.width, height: r.height };
  }
  // Fallback (no Worker/OffscreenCanvas): main-thread canvas, same contract.
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const w = bmp.width, h = bmp.height;
    const scale = Math.min(1, UPLOAD_MAX_SIDE / Math.max(w, h));
    if (scale >= 1 && isJpeg && file.size <= 1400 * 1024) {
      bmp.close?.();
      return { blob: file, meta, compressed: false, width: w, height: h }; // already small
    }
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(w * scale));
    c.height = Math.max(1, Math.round(h * scale));
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    bmp.close?.();
    const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", UPLOAD_JPEG_QUALITY));
    if (!blob) return { blob: file, meta, compressed: false, width: w, height: h };
    return { blob, meta, compressed: true, width: c.width, height: c.height };
  } catch {
    return { blob: file, meta, compressed: false, width: 0, height: 0 };
  }
}

// "Upload full-size originals on Wi-Fi only" — opt-in tenant setting, cached
// locally by loadUploadPrefs so the capture path stays synchronous.
export function originalsWifiOnly() {
  try { return localStorage.getItem("bf.up.origWifi") === "1"; } catch { return false; }
}

// "High-res photos" — opt-in 4K stream + ImageCapture.takePhoto(). OFF by
// default (P0-2): full-sensor captures OOM-killed Android Chrome in the field.
export function highResPhotos() {
  try { return localStorage.getItem("bf.up.highRes") === "1"; } catch { return false; }
}

export async function loadUploadPrefs() {
  try {
    const { data } = await api.get("/tenant/settings");
    localStorage.setItem("bf.up.origWifi", data.originals_wifi_only ? "1" : "0");
    localStorage.setItem("bf.up.highRes", data.high_res_photos ? "1" : "0");
  } catch { /* keep last known value */ }
}

// True when we're (probably) on unmetered Wi-Fi. The Network Information API
// is missing on iOS Safari — there we treat "online" as OK rather than never
// uploading originals (the setting is opt-in, so its owner wants originals).
export function isOnWifi() {
  try {
    const c = navigator.connection;
    if (!c) return true;
    if (c.type) return c.type === "wifi";
    if (c.saveData) return false;
    return c.effectiveType === "4g";
  } catch { return true; }
}
